import React, { useEffect, useState } from "react";

import {
  formatNumericCurrency,
  useAddFieldCropPractice,
  useGetFarms,
  useGetFieldPractices,
  useGetPracticeSeedings,
  useTranslation,
  useValidateCropPeriods,
} from "utils";

import { CurrencyCode, HARVEST_UNIT_VALUE } from "consts";

import { ACCOUNTING_METHODOLOGY, CROP_TYPE_FCPO, FieldCropRecommendation, PRACTICE_SLUG } from "models";

import {
  AlertBanner,
  Button,
  Divider,
  Flex,
  Form,
  Icon,
  OptGroup,
  Option,
  Radio,
  RadioChangeEvent,
  RadioPill,
  RadioPillGroup,
  Select,
  Typography,
} from "components";

export type CropRecommendationCardFormProps = {
  recommendations: FieldCropRecommendation[];
  cropType: CROP_TYPE_FCPO;
  harvestEndDate: string;
  seedingStartDate: string;
  fieldId: string;
  onClose: () => void;
  year?: number;
  fieldSize?: number;
};

export const CropRecommendationCardForm = ({
  recommendations,
  cropType,
  harvestEndDate,
  seedingStartDate,
  onClose,
  year,
  fieldId,
}: CropRecommendationCardFormProps) => {
  const { t } = useTranslation(["fieldTimeline", "practices"]);
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const [form] = Form.useForm();
  const [selectedCropId, setSelectedCropId] = useState<string | undefined>();
  const [selectionResiduePayout, setSelectionResiduePayout] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isTillageAndSeedingBundleActive, setIsTillageAndSeedingBundleActive] = useState(false);
  const { mutate: addFieldCropPractice } = useAddFieldCropPractice({ fieldId });
  const { data: fieldPractices } = useGetFieldPractices({ fieldId: fieldId || "", inView: true });
  const { validatePeriods } = useValidateCropPeriods();

  const { currentFarm } = useGetFarms();
  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const selectedRecommendation = recommendations.find((recommendation) => recommendation.cropId === selectedCropId);

  const handleSave = async (formData: { cropId: string; hasResidue?: boolean; seedingPracticeType?: string }) => {
    if (!fieldId || !fieldPractices) return;

    setIsLoading(true);

    const newCropFromRecommendation = {
      cropId: formData.cropId,
      cropType,
      harvestEndDate: harvestEndDate,
      seedingStartDate: seedingStartDate,
      seedingPracticeType: formData.seedingPracticeType ?? null,
      hasResidue: !!formData.hasResidue,
      yieldPerHa: null,
      yieldPerHaUnit: HARVEST_UNIT_VALUE.tonnesPerHa,
      seedingVerificationStatus: null,
    };

    const periodValidationErrors = validatePeriods(
      {
        ...newCropFromRecommendation,
        id: "",
        cropName: selectedRecommendation ? t(`practices:${PRACTICE_SLUG.CROPS}.${selectedRecommendation.translationSlug}`) : "",
        isConfirmed: false,
        translationSlug: "",
        harvestEndDate: harvestEndDate,
        seedingEndDate: "",
        harvestStartDate: "",
        seedingStartDate: seedingStartDate,
        seedingVerificationStatus: null,
        fallbackDates: {
          seedingStartDate: seedingStartDate || null,
          harvestEndDate: harvestEndDate || null,
        },
      },
      fieldPractices.crops
    );
    if (periodValidationErrors.length > 0) return;

    addFieldCropPractice({
      requestBody: {
        crop: newCropFromRecommendation,
        practices: [],
        hasFertilizerManagement: null,
      },
    });

    setIsLoading(false);
    onClose();
  };

  const onCropSelectionChanged = (e: RadioChangeEvent) => {
    form.resetFields();
    form.setFieldsValue({ cropId: e.target.value });
    setSelectedCropId(e.target.value);
  };

  useEffect(() => {
    if (!selectedCropId || !selectedRecommendation) return;
    if (typeof selectedRecommendation.payoutDifference.withResidue !== "number" || typeof selectedRecommendation.payoutDifference.withoutResidue !== "number") {
      setSelectionResiduePayout(0);
      return;
    }

    const residuePayoutPotential = selectedRecommendation.payoutDifference.withResidue - selectedRecommendation.payoutDifference.withoutResidue;
    setSelectionResiduePayout(residuePayoutPotential);
  }, [recommendations, selectedCropId, selectedRecommendation]);

  useEffect(() => {
    setIsTillageAndSeedingBundleActive(!!fieldPractices?.availablePracticeBundles.find((practiceBundle) => practiceBundle.slug === "tillages")?.active);
  }, [fieldPractices]);

  return (
    <Form
      data-testid="crop-recommendation-card-form"
      form={form}
      onFinish={handleSave}
      name="crop-recommendation-card-form"
      className="crop-recommendation-card-form"
    >
      <Flex className="crop-recommendation-card-form header" justify="space-between">
        <Flex gap=".5rem" align="center" justify="center">
          <Icon className="bulb-icon" iconName="tabler/bulb" />
          <Typography variant="h5">{t("cropCard.recommendations.form.title")}</Typography>
        </Flex>
        <Icon className="pointer" iconName="tabler/x" onClick={onClose} />
      </Flex>
      {year && (
        <Typography variant="paragraph-m" bold className="margin-top-200">
          {t("cropCard.recommendations.form.subtitle", { year })}
        </Typography>
      )}
      <Typography variant="paragraph-m" className="margin-top-125">
        {t("cropCard.recommendations.form.selectLabel")}
      </Typography>
      <Form.Item name="cropId" className="margin-top-125" rules={[{ required: true, message: t("addEditCrop.selectionRequiredErrorMessage") }]}>
        <RadioPillGroup onChange={onCropSelectionChanged} value={selectedCropId}>
          {recommendations.map((recommendedCrop) => (
            <RadioPill
              dataTestId={`crop-recommendation-card-form-radio-group-${recommendedCrop.cropId}`}
              className="recommendation-card-form-crop-option"
              additionalInfo={
                isInventoryProgram
                  ? undefined
                  : `(${t("addEditCrop.avgPayout")} ${getPayoutString(recommendedCrop.payoutDifference.withoutResidue, currentFarm?.carbonProgram?.currency)})`
              }
              key={recommendedCrop.cropId}
              label={t(`practices:${PRACTICE_SLUG.CROPS}.${recommendedCrop.translationSlug}`)}
              value={recommendedCrop.cropId}
            />
          ))}
        </RadioPillGroup>
      </Form.Item>
      {selectedRecommendation?.hasResidue && selectedCropId ? (
        <>
          <Divider className="margin-top-125 margin-bottom-100" />
          <Typography variant="paragraph-m" className="residue-label">
            {t("addEditCrop.residueLeft")}
          </Typography>
          {selectionResiduePayout !== 0 && (
            <AlertBanner
              dataTestId="crop-recommendation-card-form-residue-banner"
              className="margin-bottom-100"
              description={selectionResiduePayout > 0 ? t("addEditCrop.leaveResidueInfo") : t("addEditCrop.removeResidueInfo")}
              type="recommended"
              iconName="tabler/bulb"
            />
          )}
          <Form.Item name="hasResidue" initialValue={undefined} rules={[{ required: true, message: t("addEditCrop.selectionRequiredErrorMessage") }]}>
            <Radio.Group
              data-testid="crop-recommendation-card-form-residue-radio-group"
              className="recommendation-card-form radio-button-group"
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  value: true,
                  label: (
                    <Flex vertical justify="space-around">
                      <Typography variant="paragraph-m">{t("addEditCrop.yes")}</Typography>
                      {typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && !isInventoryProgram && (
                        <Typography variant="paragraph-s" data-testid="crop-recommendation-card-form-residue-payout-option-yes" color="grey-600">
                          {t("addEditCrop.avgPayout")} {getPayoutString(selectionResiduePayout, currentFarm?.carbonProgram?.currency)}
                        </Typography>
                      )}
                    </Flex>
                  ),
                },
                {
                  value: false,
                  label: (
                    <Flex vertical justify="space-around">
                      <Typography variant="paragraph-m">{t("addEditCrop.no")}</Typography>
                      {typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && !isInventoryProgram && (
                        <Typography variant="paragraph-s" color="grey-600">
                          {formatNumericCurrency(0, { decimals: 0, currency: currentFarm?.carbonProgram?.currency })}
                        </Typography>
                      )}
                    </Flex>
                  ),
                },
              ]}
            />
          </Form.Item>
        </>
      ) : null}
      {cropType !== CROP_TYPE_FCPO.UNDERSOWING && selectedCropId && isTillageAndSeedingBundleActive ? (
        <>
          <Divider className="margin-top-125 margin-bottom-100" />
          <Typography variant="paragraph-m" className="residue-label">
            {t("cropCard.recommendations.form.seedingTypeLabel")}
          </Typography>
          <div>
            <Select
              dataTestId="crop-recommendation-card-form-seeding-type-selection"
              label={t("addEditCrop.seedingType")}
              name="seedingPracticeType"
              rules={[{ message: t("addEditCrop.selectionRequiredErrorMessage"), required: true }]}
              showSearch
            >
              <OptGroup>
                {seedingPracticesQuery.data &&
                  seedingPracticesQuery.data.map((option, index) => (
                    <Option data-testid={`seeding-selection-option-${index}`} key={option.id} value={option.id}>
                      {option.name}
                    </Option>
                  ))}
              </OptGroup>
            </Select>
          </div>
        </>
      ) : null}
      <Flex justify="flex-end" align="center" gap="1rem" className="margin-top-200">
        <Button
          dataTestId="crop-recommendation-card-form-save-button"
          className="crop-recommendation-card-form save-button"
          type="primary"
          size="small"
          htmlType="submit"
          loading={isLoading}
          disabled={isLoading}
        >
          {t("addEditCrop.save")}
        </Button>
      </Flex>
    </Form>
  );
};

const formatPayoutValue = (value: number, currency?: CurrencyCode): string => {
  if (!value) return formatNumericCurrency(0, { currency, decimals: 0 });
  return formatNumericCurrency(value, { currency, decimals: 0, signDisplay: "always" });
};

const getPayoutString = (payout: number, currency?: CurrencyCode) => formatPayoutValue(payout, currency);
